import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  CONTACT_WARNING,
  ContactComplianceRefusal,
  DNC_BLOCKED,
  DNC_CHECK_QUEUED,
  LITIGATOR_BLOCKED,
  OutboundGuardOptions,
  QUIET_HOURS_BLOCKED,
  QUIET_HOURS_CONFIRM_REQUIRED,
  QuietHoursBlockedError,
} from "@/model/interface/Compliance";
import { DncCheckMode } from "@/enum/DncCheckMode";

type GuardedAction = (options: OutboundGuardOptions) => Promise<unknown>;

const COMPLIANCE_CODES: string[] = [CONTACT_WARNING, DNC_CHECK_QUEUED, DNC_BLOCKED, LITIGATOR_BLOCKED];

const INITIAL_OPTIONS: OutboundGuardOptions = {
  acknowledgeQuietHours: false,
  acknowledgeContactWarning: false,
  suppressContactWarning: false,
};

function errorBody(error: unknown): Record<string, unknown> | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const body = "data" in error ? (error as { data?: unknown }).data : error;
  if (!body || typeof body !== "object") {
    return null;
  }
  return body as Record<string, unknown>;
}

function asQuietHoursError(body: Record<string, unknown>): QuietHoursBlockedError | null {
  if (body.code !== QUIET_HOURS_CONFIRM_REQUIRED && body.code !== QUIET_HOURS_BLOCKED) {
    return null;
  }
  const partial = body as Partial<QuietHoursBlockedError>;
  return {
    code: body.code,
    detail: partial.detail ?? "This contact is outside the recipient's calling hours.",
    overridable: partial.overridable ?? body.code === QUIET_HOURS_CONFIRM_REQUIRED,
    blocked: partial.blocked ?? [],
  };
}

function asComplianceRefusal(body: Record<string, unknown>): ContactComplianceRefusal | null {
  if (typeof body.code !== "string" || !COMPLIANCE_CODES.includes(body.code)) {
    return null;
  }
  const partial = body as Partial<ContactComplianceRefusal>;
  return {
    code: partial.code ?? CONTACT_WARNING,
    detail: partial.detail ?? "This contact needs a compliance check.",
    phoneNumbers: partial.phoneNumbers ?? [],
    instantPrice: partial.instantPrice,
    delayedPrice: partial.delayedPrice,
    warning: partial.warning,
    learnMoreUrl: partial.learnMoreUrl,
  };
}

export function useOutboundGuard() {
  const [quietHoursError, setQuietHoursError] = useState<QuietHoursBlockedError | null>(null);
  const [complianceRefusal, setComplianceRefusal] = useState<ContactComplianceRefusal | null>(null);
  const pendingAction = useRef<GuardedAction | null>(null);
  const pendingOptions = useRef<OutboundGuardOptions>(INITIAL_OPTIONS);

  const attempt = async (action: GuardedAction, options: OutboundGuardOptions) => {
    try {
      await action(options);
      pendingAction.current = null;
      pendingOptions.current = INITIAL_OPTIONS;
    } catch (error) {
      const body = errorBody(error);
      const quietHours = body ? asQuietHoursError(body) : null;
      const compliance = body ? asComplianceRefusal(body) : null;
      if (!quietHours && !compliance) {
        pendingAction.current = null;
        pendingOptions.current = INITIAL_OPTIONS;
        throw error;
      }
      if (compliance?.code === DNC_BLOCKED) {
        pendingAction.current = null;
        pendingOptions.current = INITIAL_OPTIONS;
        const learnMoreUrl = compliance.learnMoreUrl;
        toast.error(compliance.detail, {
          action: learnMoreUrl
            ? { label: "Learn more", onClick: () => window.open(learnMoreUrl, "_blank", "noreferrer") }
            : undefined,
        });
        return;
      }
      pendingAction.current = action;
      pendingOptions.current = options;
      setQuietHoursError(quietHours);
      setComplianceRefusal(compliance);
    }
  };

  const guard = async (action: GuardedAction) => {
    await attempt(action, INITIAL_OPTIONS);
  };

  const retryWith = async (changes: Partial<OutboundGuardOptions>) => {
    const action = pendingAction.current;
    setQuietHoursError(null);
    setComplianceRefusal(null);
    if (!action) {
      return;
    }
    await attempt(action, { ...pendingOptions.current, ...changes });
  };

  const proceedQuietHours = () => retryWith({ acknowledgeQuietHours: true });

  const acknowledgeContactWarning = (suppressFromNowOn: boolean) =>
    retryWith({ acknowledgeContactWarning: true, suppressContactWarning: suppressFromNowOn });

  const chooseDncCheck = (mode: DncCheckMode) => retryWith({ dncCheck: mode });

  const cancel = () => {
    pendingAction.current = null;
    pendingOptions.current = INITIAL_OPTIONS;
    setQuietHoursError(null);
    setComplianceRefusal(null);
  };

  return {
    guard,
    quietHoursError,
    complianceRefusal,
    proceedQuietHours,
    acknowledgeContactWarning,
    chooseDncCheck,
    cancel,
  };
}

export type OutboundGuard = ReturnType<typeof useOutboundGuard>;
